import { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  ArrowLeft, School, Copy, Users, TrendingUp, Sparkles, Plus, LogIn, Trash2,
  BookOpen, Loader2, Check, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { useSubscription } from "../context/SubscriptionContext";
import TheoryView from "../components/TheoryView";
import QuizView from "../components/QuizView";

export default function ClassroomView({ onBack }) {
  const { subscription } = useSubscription();
  const [tab, setTab] = useState("mine"); // mine | detail | assignments | solve
  const [mine, setMine] = useState({ owned: [], joined: [] });
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [currentClass, setCurrentClass] = useState(null); // {class_id, name, role}
  const [assignments, setAssignments] = useState([]);
  const [currentAssignment, setCurrentAssignment] = useState(null);

  const isSchools = subscription?.plan === "schools";

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/classes/mine");
      setMine(data);
    } catch (e) {
      toast.error("Nie udało się pobrać klas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const createClass = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.post("/classes", { name });
      setNewName("");
      toast.success("Klasa utworzona");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się utworzyć klasy");
    }
  };

  const join = async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) return;
    try {
      const { data } = await api.post("/classes/join", { join_code: code });
      toast.success(`Dołączyłeś do: ${data.name}`);
      setJoinCode("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się dołączyć");
    }
  };

  const openDetail = async (class_id) => {
    try {
      const { data } = await api.get(`/classes/${class_id}/stats`);
      setDetail(data);
      setTab("detail");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Brak dostępu");
    }
  };

  const deleteClass = async (class_id) => {
    if (!window.confirm("Na pewno usunąć tę klasę?")) return;
    try {
      await api.delete(`/classes/${class_id}`);
      toast.success("Usunięto");
      setDetail(null);
      setTab("mine");
      refresh();
    } catch (e) {
      toast.error("Nie udało się usunąć");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`Kod skopiowany: ${code}`);
  };

  const openAssignments = async (c, role) => {
    try {
      const { data } = await api.get(`/classes/${c.class_id}/assignments`);
      setAssignments(data.items || []);
      setCurrentClass({ class_id: c.class_id, name: c.name, role });
      setTab("assignments");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się pobrać zadań");
    }
  };

  const openAssignment = async (assignment_id) => {
    try {
      const { data } = await api.get(`/assignments/${assignment_id}`);
      setCurrentAssignment(data);
      setTab("solve");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się pobrać zadania");
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="classroom-view">
      <header className="w-full border-b border-slate-100 sticky top-0 bg-white/80 backdrop-blur z-10">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <button
            onClick={onBack}
            data-testid="classroom-back-button"
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" /> Wróć
          </button>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <School className="w-4 h-4 text-slate-900" /> Panel klasy
          </div>
          <div className="w-16" />
        </div>
      </header>

      <main className="flex-1 max-w-[900px] w-full mx-auto px-4 sm:px-6 py-8 sm:py-12 ss-fadein">
        {tab === "mine" && (
          <>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
              <School className="w-3.5 h-3.5" /> Klasa
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
              Ucz się razem
            </h1>
            <p className="mt-2 text-slate-500">
              Utwórz klasę (plan Szkoły) i zaproś uczniów kodem, żeby śledzić ich postępy. Lub dołącz do klasy, do której zaprasza Cię nauczyciel.
            </p>

            {/* Create class */}
            <section className={`mt-8 rounded-3xl p-5 sm:p-6 ${isSchools ? "bg-[#FEF9C3] border border-yellow-200" : "bg-slate-50 border border-slate-200"}`}>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
                <Plus className="inline w-3.5 h-3.5 mr-1" /> Utwórz klasę (nauczyciel)
              </div>
              {isSchools ? (
                <div className="flex flex-col sm:flex-row gap-2 mt-2">
                  <input
                    data-testid="new-class-name-input"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Np. Klasa 8A – Biologia"
                    className="flex-1 rounded-2xl border-2 border-slate-200 focus:border-[#FFD600] outline-none px-4 py-3 bg-white"
                  />
                  <button
                    data-testid="create-class-button"
                    onClick={createClass}
                    className="inline-flex items-center justify-center gap-2 bg-[#FFD600] hover:bg-[#E6C200] text-slate-900 font-bold px-5 py-3 rounded-2xl"
                  >
                    Utwórz klasę
                  </button>
                </div>
              ) : (
                <p className="text-sm text-slate-600 mt-2">
                  Tworzenie klas jest dostępne w planie <b>Szkoły</b>. Uczniowie i studenci mogą swobodnie dołączać do klas kodem – nie potrzebują tego planu.
                </p>
              )}
            </section>

            {/* Join class */}
            <section className="mt-4 rounded-3xl p-5 sm:p-6 bg-white border border-slate-200">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
                <LogIn className="inline w-3.5 h-3.5 mr-1" /> Dołącz do klasy (uczeń)
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mt-2">
                <input
                  data-testid="join-code-input"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="Kod klasy (np. EQXN5G)"
                  className="flex-1 rounded-2xl border-2 border-slate-200 focus:border-[#FFD600] outline-none px-4 py-3 font-mono tracking-widest uppercase"
                />
                <button
                  data-testid="join-class-button"
                  onClick={join}
                  className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold px-5 py-3 rounded-2xl"
                >
                  Dołącz
                </button>
              </div>
            </section>

            {loading ? (
              <div className="mt-10 flex justify-center">
                <div className="flex gap-1.5">
                  {[0,1,2].map(i => (
                    <span key={i} className="w-2.5 h-2.5 rounded-full bg-[#FFD600] ss-dot" style={{animationDelay:`${i*0.18}s`}}/>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {mine.owned.length > 0 && (
                  <section className="mt-10" data-testid="owned-classes">
                    <h2 className="text-2xl font-bold tracking-tight text-slate-900">Moje klasy (jako nauczyciel)</h2>
                    <ul className="mt-4 space-y-3">
                      {mine.owned.map((c) => (
                        <li
                          key={c.class_id}
                          className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-slate-900 truncate">{c.name}</div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              <Users className="inline w-3 h-3 mr-1" />
                              {c.members_count} {c.members_count === 1 ? "uczeń" : "uczniów"}
                            </div>
                          </div>
                          <button
                            data-testid={`copy-code-${c.class_id}`}
                            onClick={() => copyCode(c.join_code)}
                            className="inline-flex items-center gap-1.5 text-xs font-mono tracking-widest bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5 hover:border-slate-900"
                          >
                            {c.join_code} <Copy className="w-3 h-3" />
                          </button>
                          <button
                            data-testid={`assignments-${c.class_id}`}
                            onClick={() => openAssignments(c, "teacher")}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-700 border border-slate-200 hover:border-slate-900 px-3 py-2 rounded-full"
                          >
                            <BookOpen className="w-3.5 h-3.5"/> Zadania
                          </button>
                          <button
                            data-testid={`open-class-${c.class_id}`}
                            onClick={() => openDetail(c.class_id)}
                            className="inline-flex items-center gap-1.5 bg-[#FFD600] hover:bg-[#E6C200] text-slate-900 font-bold px-4 py-2 rounded-2xl text-sm"
                          >
                            Otwórz
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {mine.joined.length > 0 && (
                  <section className="mt-8" data-testid="joined-classes">
                    <h2 className="text-2xl font-bold tracking-tight text-slate-900">Klasy, do których dołączyłeś</h2>
                    <ul className="mt-4 space-y-3">
                      {mine.joined.map((c) => (
                        <li
                          key={c.class_id}
                          className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4"
                        >
                          <div className="flex-1">
                            <div className="font-bold text-slate-900">{c.name}</div>
                            <div className="text-xs text-slate-500">Uczeń</div>
                          </div>
                          <button
                            data-testid={`joined-assignments-${c.class_id}`}
                            onClick={() => openAssignments(c, "member")}
                            className="inline-flex items-center gap-1.5 bg-[#FFD600] hover:bg-[#E6C200] text-slate-900 font-bold px-4 py-2 rounded-2xl text-sm"
                          >
                            <BookOpen className="w-3.5 h-3.5"/> Zadania
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </>
            )}
          </>
        )}

        {tab === "detail" && detail && (
          <div data-testid="class-detail">
            <button
              onClick={() => { setTab("mine"); setDetail(null); }}
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6"
              data-testid="class-detail-back-button"
            >
              <ArrowLeft className="w-4 h-4" /> Wróć do listy klas
            </button>

            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                  <Sparkles className="w-3.5 h-3.5" /> Klasa
                </div>
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{detail.name}</h1>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => copyCode(detail.join_code)}
                  data-testid="copy-detail-code"
                  className="inline-flex items-center gap-2 bg-[#FEF9C3] border border-yellow-200 rounded-2xl px-4 py-2 text-slate-900 font-mono tracking-widest text-sm hover:bg-yellow-100"
                >
                  {detail.join_code} <Copy className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => deleteClass(detail.class_id)}
                  data-testid="delete-class-button"
                  className="p-2 text-slate-400 hover:text-red-500 rounded-xl hover:bg-red-50"
                  aria-label="Usuń klasę"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="mt-6 grid sm:grid-cols-3 gap-3">
              <StatBox label="Uczniowie" value={detail.members.length} />
              <StatBox
                label="Zrobione quizy"
                value={detail.members.reduce((a, m) => a + (m.quizzes_completed || 0), 0)}
              />
              <StatBox
                label="Średni wynik"
                value={(() => {
                  const withScores = detail.members.filter((m) => m.avg_percent != null);
                  if (!withScores.length) return "—";
                  const avg = Math.round(
                    withScores.reduce((a, m) => a + m.avg_percent, 0) / withScores.length
                  );
                  return `${avg}%`;
                })()}
              />
            </div>

            <h2 className="mt-10 text-2xl font-bold tracking-tight text-slate-900">Uczniowie</h2>
            {detail.members.length === 0 ? (
              <div className="mt-4 bg-slate-50 border border-slate-200 rounded-3xl p-6 text-center text-slate-500">
                Jeszcze nikt nie dołączył. Podaj kod uczniom: <b className="font-mono">{detail.join_code}</b>
              </div>
            ) : (
              <ul className="mt-4 space-y-3">
                {detail.members.map((m) => (
                  <li key={m.user_id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`member-${m.user_id}`}>
                    <div className="flex items-center gap-3">
                      {m.picture ? (
                        <img src={m.picture} alt={m.name} className="w-9 h-9 rounded-full" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-500">
                          {(m.name || "?").slice(0, 1)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-900 truncate">{m.name}</div>
                        <div className="text-xs text-slate-500 truncate">{m.email}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-slate-500">Quizy</div>
                        <div className="font-bold text-slate-900">{m.quizzes_completed}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-slate-500">Średnia</div>
                        <div className="font-bold text-slate-900">
                          {m.avg_percent != null ? `${m.avg_percent}%` : "—"}
                        </div>
                      </div>
                    </div>
                    {m.recent?.length > 0 && (
                      <details className="mt-3">
                        <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-900">
                          Ostatnie quizy ({m.recent.length})
                        </summary>
                        <ul className="mt-2 space-y-1">
                          {m.recent.map((r, i) => (
                            <li key={i} className="text-sm text-slate-600 flex justify-between gap-2">
                              <span className="truncate">{r.topic}</span>
                              <span className="flex-shrink-0 font-bold text-slate-900">
                                {r.score}/{r.total}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "assignments" && currentClass && (
          <AssignmentsTab
            cls={currentClass}
            items={assignments}
            onBack={() => { setTab("mine"); setCurrentClass(null); setAssignments([]); }}
            onOpen={openAssignment}
            onRefresh={() => openAssignments(currentClass, currentClass.role)}
          />
        )}

        {tab === "solve" && currentAssignment && (
          <AssignmentSolve
            assignment={currentAssignment}
            onBack={() => { setTab("assignments"); setCurrentAssignment(null); openAssignments(currentClass, currentClass.role); }}
            onSubmit={async (answers) => {
              const { data } = await api.post(`/assignments/${currentAssignment.assignment_id}/attempt`, { answers });
              return data;
            }}
          />
        )}
      </main>
    </div>
  );
}

/* -------- Assignments list -------- */
function AssignmentsTab({ cls, items, onBack, onOpen, onRefresh }) {
  const [topic, setTopic] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async (e) => {
    e?.preventDefault?.();
    if (topic.trim().length < 3) { toast.error("Podaj temat"); return; }
    setBusy(true);
    try {
      await api.post(`/classes/${cls.class_id}/assignments`, { topic: topic.trim(), title: title.trim() });
      toast.success("Zadanie utworzone — uczniowie już mogą je rozwiązać");
      setTopic(""); setTitle("");
      onRefresh();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Nie udało się utworzyć zadania");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="assignments-tab">
      <button
        onClick={onBack}
        data-testid="assignments-back"
        className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Wróć do klas
      </button>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
        <BookOpen className="w-3.5 h-3.5" /> {cls.role === "teacher" ? "Zadania dla klasy" : "Zadania od nauczyciela"}
      </div>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{cls.name}</h1>
      {cls.role === "member" && (
        <p className="mt-2 text-sm text-slate-500">
          Wszystkie zadania w tej klasie masz za darmo – nawet jeśli nie masz subskrypcji.
        </p>
      )}

      {cls.role === "teacher" && (
        <form onSubmit={create} className="mt-6 bg-white border border-slate-200 rounded-3xl p-5 sm:p-6 grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2 text-sm text-slate-500">
            Wygeneruj dla swojej klasy teorię + quiz na dowolny temat. Każdy uczeń z klasy (nawet bez subskrypcji) będzie mógł to rozwiązać.
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Temat *</label>
            <input
              value={topic} onChange={(e) => setTopic(e.target.value)}
              data-testid="assignment-topic"
              placeholder="np. Fotosynteza"
              className="w-full rounded-2xl border-2 border-slate-200 focus:border-[#FFD600] outline-none px-4 py-3 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Tytuł zadania (opcjonalnie)</label>
            <input
              value={title} onChange={(e) => setTitle(e.target.value)}
              data-testid="assignment-title"
              placeholder="np. Praca klasowa 1"
              className="w-full rounded-2xl border-2 border-slate-200 focus:border-[#FFD600] outline-none px-4 py-3 text-sm"
            />
          </div>
          <div className="sm:col-span-2">
            <button
              type="submit" disabled={busy}
              data-testid="assignment-create"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#FFD600] hover:bg-[#E6C200] text-slate-900 font-bold px-6 py-3.5 rounded-2xl disabled:opacity-60"
            >
              {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> AI generuje teorię i quiz...</> : <><Plus className="w-4 h-4" strokeWidth={2.5}/> Utwórz zadanie</>}
            </button>
          </div>
        </form>
      )}

      <section className="mt-8">
        <h2 className="text-lg font-bold tracking-tight text-slate-900 mb-3">Lista zadań ({items.length})</h2>
        {items.length === 0 ? (
          <div className="bg-slate-50 border border-slate-200 rounded-3xl p-10 text-center">
            <BookOpen className="w-10 h-10 mx-auto text-slate-300 mb-3"/>
            <div className="text-slate-700 font-bold">Jeszcze nie ma zadań</div>
            <div className="text-sm text-slate-500 mt-1">
              {cls.role === "teacher" ? "Utwórz pierwsze zadanie formularzem powyżej." : "Nauczyciel jeszcze nic nie dodał."}
            </div>
          </div>
        ) : (
          <ul className="space-y-2.5" data-testid="assignments-list">
            {items.map((a) => (
              <li key={a.assignment_id}>
                <button
                  data-testid={`assignment-open-${a.assignment_id}`}
                  onClick={() => onOpen(a.assignment_id)}
                  className="w-full text-left bg-white border border-slate-200 hover:border-slate-900 transition-colors rounded-2xl p-4 flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#FEF9C3] flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-5 h-5 text-slate-900" strokeWidth={2.5}/>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-slate-900 truncate">{a.title || a.topic}</div>
                    <div className="text-xs text-slate-500 truncate flex items-center gap-1.5">
                      <Clock className="w-3 h-3"/> {new Date(a.created_at).toLocaleDateString("pl-PL")}
                      {a.topic && <> · <span className="truncate">{a.topic}</span></>}
                    </div>
                  </div>
                  {a.my_attempt ? (
                    <span className="inline-flex items-center gap-1 text-xs font-bold bg-green-50 border border-green-200 text-green-700 rounded-full px-2.5 py-1">
                      <Check className="w-3 h-3"/> {a.my_attempt.score}/{a.my_attempt.total}
                    </span>
                  ) : cls.role === "member" ? (
                    <span className="inline-flex items-center gap-1 text-xs font-bold bg-[#FFD600] text-slate-900 rounded-full px-2.5 py-1">
                      Rozwiąż
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/* -------- Assignment Solve (theory → quiz → result) -------- */
function AssignmentSolve({ assignment, onBack, onSubmit }) {
  const [phase, setPhase] = useState(assignment.my_attempt ? "done" : "theory");
  // "theory" | "quiz" | "done"
  const [result, setResult] = useState(assignment.my_attempt || null);

  if (phase === "theory") {
    return (
      <div data-testid="assignment-theory">
        <button
          onClick={onBack}
          data-testid="assignment-back"
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Zadania klasy
        </button>
        <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
          {assignment.class_name} · zadanie
        </div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mb-6">
          {assignment.title || assignment.topic}
        </h1>
        <TheoryView
          theory={assignment.theory}
          onStartQuiz={() => setPhase("quiz")}
          onBack={onBack}
        />
      </div>
    );
  }

  if (phase === "quiz") {
    return (
      <div data-testid="assignment-quiz">
        <button
          onClick={() => setPhase("theory")}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Wróć do teorii
        </button>
        <QuizView
          questions={assignment.quiz?.questions || []}
          title={assignment.title || assignment.topic}
          onFinish={async (userAnswers) => {
            // userAnswers: array of selected indices per question
            const answers = (userAnswers || []).map((selected_index, question_index) => ({
              question_index, selected_index,
            }));
            try {
              const data = await onSubmit(answers);
              setResult(data);
              setPhase("done");
            } catch (e) {
              toast.error("Nie udało się zapisać wyniku");
            }
          }}
        />
      </div>
    );
  }

  // done
  const score = result?.score ?? 0;
  const total = result?.total ?? ((assignment.quiz?.questions || []).length);
  const pct = total > 0 ? Math.round((score / total) * 100) : 0;
  return (
    <div data-testid="assignment-done" className="max-w-xl mx-auto text-center py-10">
      <div className="w-20 h-20 rounded-3xl bg-[#FFD600] flex items-center justify-center mx-auto mb-5">
        <Check className="w-10 h-10 text-slate-900" strokeWidth={3}/>
      </div>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
        Twój wynik: {score} / {total}
      </h1>
      <div className="mt-2 text-slate-500">{pct}% poprawnych</div>
      <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
        <button
          onClick={() => { setResult(null); setPhase("theory"); }}
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold px-5 py-3 rounded-2xl"
        >
          Przerób jeszcze raz
        </button>
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 border border-slate-200 hover:border-slate-900 text-slate-900 font-bold px-5 py-3 rounded-2xl"
        >
          Wróć do zadań
        </button>
      </div>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-yellow-500" />
        {value}
      </div>
    </div>
  );
}
